import { useEffect } from "react";
import { useSnackbar } from "notistack";
import { useSocketContext } from "@/context/socket.context/socket.context";
import useAuthUser from "@/hooks/useAuthUser";
import { getLeadDetailsUrl, PATH_INBOX } from "@/routes/paths";
import { getLeadInteractions } from "@/services/interactions.service";
import leadsService from "@/services/leads.service";
import { useAuthStore } from "@/store/auth/auth.store";
import { useInboxStore } from "@/store/inbox/inbox.store";
import { useSmsRefreshStore } from "@/store/sms/sms-refresh.store";

// Local wire strings until @icebergeradmin/common carries them; meetings-back
// emits all of these to the company room (see its docs/socket-events.md).
export const SOCKET_EVENT_SMS_RECEIVED = "sms.received";
export const SOCKET_EVENT_SMS_STATUS_CHANGED = "sms.status_changed";
export const SOCKET_EVENT_SMS_SENT = "sms.sent";
export const SOCKET_EVENT_CONVERSATION_UPDATED = "inbox.conversation_updated";

/**
 * Ids only: the company socket room is joined without authentication, so the
 * event never carries the text, the number or the name — those are fetched
 * through the authenticated API before the toast shows.
 */
export type TSmsReceivedEvent = {
  leadId: number;
  /** The agent whose line was texted; null for a company-line text. */
  agentUserId: number | null;
  /** Who the toast is for: the line's agent, else the thread's assignee, else the lead's owner. */
  notifyUserId: number | null;
  interactionId: number;
  /** The Inbox thread the text landed in; null when its copy could not be stored. */
  conversationId: number | null;
};

type TSmsStatusChangedEvent = {
  leadId: number;
  interactionId: number;
  conversationId: number | null;
  status: string;
};

/** A teammate's (or this user's own) outbound text went out; null ids for a text to a non-lead. */
type TSmsSentEvent = {
  leadId: number | null;
  interactionId: number | null;
  conversationId: number | null;
  senderUserId: number;
};

type TConversationUpdatedEvent = { conversationId: number };

/** What the toast shows, resolved through the API from the event's ids. */
export type TSmsToast = {
  leadId: number;
  interactionId: number;
  name: string;
  preview: string;
  /** Lead's profile photo URL, when the backend includes it. */
  leadAvatar?: string | null;
  /** Where the click lands: the Inbox thread when the viewer can open it, else Lead Details. */
  targetUrl: string;
};

const AUTO_HIDE_MS = 10_000;
const PREVIEW_CHARS = 120;

const showPushNotification = (toast: TSmsToast) => {
  if (!("Notification" in window) || Notification.permission !== "granted") return;
  const notification = new Notification(`New text from ${toast.name}`, {
    body: toast.preview,
    icon: "/favicon.ico",
    tag: `sms-${toast.interactionId}`,
  });
  notification.onclick = () => {
    window.focus();
    window.location.href = toast.targetUrl;
    notification.close();
  };
};

/** The event's ids resolved to a name and a preview; empty strings when the API can't say. */
const resolveToast = async (sms: TSmsReceivedEvent, targetUrl: string): Promise<TSmsToast> => {
  let name = "";
  let preview = "";
  let leadAvatar: string | null = null;
  try {
    const [lead, interactions] = await Promise.all([
      leadsService.getById(sms.leadId),
      getLeadInteractions(sms.leadId, 1, 5, "sms"),
    ]);
    name = `${lead.firstName ?? ""} ${lead.lastName ?? ""}`.trim();
    leadAvatar = lead.avatar ?? null;
    // Only the exact interaction: in a burst, quoting "the latest inbound" would caption
    // this toast with another message's text.
    const match = interactions.data.find((i) => i.id === sms.interactionId);
    preview = match?.sms?.body?.slice(0, PREVIEW_CHARS) ?? "";
  } catch {
    // The toast still opens the lead; it just can't quote the text.
  }
  return {
    leadId: sms.leadId,
    interactionId: sms.interactionId,
    name: name || "A lead",
    preview: preview || "sent you a text message",
    leadAvatar,
    targetUrl,
  };
};

/**
 * Global listener for the messaging socket events. Every event refreshes the
 * lead's SMS surfaces and, for users who can see the Inbox, its list, badge and
 * open thread. A lead's text notifies the line's agent or, on the company line,
 * whoever the CRM picked (`notifyUserId`: the thread's assignee, else the
 * lead's owner); the toast opens the Inbox thread when the viewer can see it.
 */
export const useInboundSmsNotifier = () => {
  const socket = useSocketContext();
  const { enqueueSnackbar } = useSnackbar();
  const userId = useAuthStore((s) => s.user?.id);
  const bump = useSmsRefreshStore((s) => s.bump);
  const { user, hasPermission } = useAuthUser();
  const canInbox = Boolean(user?.isAdmin) || hasPermission("inbox:access");

  // Any lead-side SMS activity (sends from Lead Details included, which bump) also
  // refreshes the Inbox, without the stores importing each other.
  useEffect(() => {
    if (!canInbox) return;
    return useSmsRefreshStore.subscribe(() => useInboxStore.getState().pokeFromEvent());
  }, [canInbox]);

  useEffect(() => {
    /** Inbox refresh for one event; a no-op for users who cannot open the Inbox. */
    const poke = (conversationId: number | null | undefined) => {
      if (!canInbox) return;
      useInboxStore.getState().pokeFromEvent(typeof conversationId === "number" ? conversationId : undefined);
    };

    const handleReceived = (sms: TSmsReceivedEvent) => {
      // The socket is untyped: a malformed event must not seed the store or navigate to /leads/undefined.
      if (typeof sms?.leadId !== "number" || typeof sms?.interactionId !== "number") return;
      bump(sms.leadId);
      poke(sms.conversationId);
      if (!userId || (sms.agentUserId !== userId && sms.notifyUserId !== userId)) return;
      const targetUrl =
        canInbox && typeof sms.conversationId === "number"
          ? `${PATH_INBOX}/${sms.conversationId}`
          : getLeadDetailsUrl(sms.leadId);
      void resolveToast(sms, targetUrl).then((toast) => {
        // Hidden tab with push permission → native notification. Without the permission the
        // snackbar still queues, so the text is waiting if they come back within its lifetime.
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          showPushNotification(toast);
          return;
        }
        enqueueSnackbar("", {
          variant: "newSmsNotification",
          sms: toast,
          // A webhook replay re-emits the event; one toast per text.
          key: `sms-${toast.interactionId}`,
          preventDuplicate: true,
          autoHideDuration: AUTO_HIDE_MS,
          anchorOrigin: { vertical: "bottom", horizontal: "left" },
        });
      });
    };

    const handleStatusChanged = (event: TSmsStatusChangedEvent) => {
      if (typeof event?.leadId !== "number") return;
      bump(event.leadId);
      poke(event.conversationId);
    };

    /** A teammate's send (or this user's own from another surface): refresh without toasting. */
    const handleSent = (event: TSmsSentEvent) => {
      if (typeof event?.senderUserId !== "number") return;
      if (typeof event.leadId === "number") bump(event.leadId);
      poke(event.conversationId);
    };

    const handleConversationUpdated = (event: TConversationUpdatedEvent) => {
      if (typeof event?.conversationId !== "number") return;
      poke(event.conversationId);
    };

    socket.on(SOCKET_EVENT_SMS_RECEIVED, handleReceived);
    socket.on(SOCKET_EVENT_SMS_STATUS_CHANGED, handleStatusChanged);
    socket.on(SOCKET_EVENT_SMS_SENT, handleSent);
    socket.on(SOCKET_EVENT_CONVERSATION_UPDATED, handleConversationUpdated);
    return () => {
      socket.off(SOCKET_EVENT_SMS_RECEIVED, handleReceived);
      socket.off(SOCKET_EVENT_SMS_STATUS_CHANGED, handleStatusChanged);
      socket.off(SOCKET_EVENT_SMS_SENT, handleSent);
      socket.off(SOCKET_EVENT_CONVERSATION_UPDATED, handleConversationUpdated);
    };
  }, [socket, enqueueSnackbar, userId, bump, canInbox]);
};
